import React from 'react';
import Footer from './Footer';

export default function DendrometerFundamentals() {
  return (
    <div className="content-page">
      <div className="content-container">
        <div className="content-hero">
          <h1>Dendrometer Fundamentals</h1>
          <p className="page-subtitle">Understanding plant stress through precision measurements</p>
        </div>

        <div className="content-section">
          <h2>What is a Dendrometer?</h2>
          <p>
            A dendrometer is a precision instrument that measures minute changes in plant diameter over time, 
            typically capturing measurements every 15 minutes. These microscopic changes reveal the plant's daily 
            rhythm and stress levels, providing insights impossible to detect with the naked eye.
          </p>
        </div>

        <div className="content-section">
          <h2>The Plant's Daily Cycle</h2>
          <p>
            A healthy plant follows a predictable daily pattern:
          </p>
          <ul>
            <li>
              <strong>Daytime (Contraction):</strong> When sunlight hits the plant's canopy, the stomata 
              (tiny pores on leaves) open to enable photosynthesis. This allows water to evaporate from 
              the leaves through transpiration, creating a "pull" that stretches the plant's sap. 
              As a result, the plant diameter contracts.
            </li>
            <li>
              <strong>Nighttime (Expansion):</strong> When the sun sets, transpiration stops and the 
              plant's sap pressure increases. The plant refills its tissues, causing the diameter to 
              expand back to (or exceeding) its previous size. This overnight recovery is a sign of 
              adequate water availability.
            </li>
          </ul>
        </div>

        <div className="content-section">
          <h2>Understanding Water Stress</h2>
          <p>
            When soil water becomes depleted, the plant's ability to refill overnight is compromised. 
            This results in:
          </p>
          <ul>
            <li>Greater daytime contraction (the plant becomes more desperate as it tries to conserve water)</li>
            <li>Reduced or absent nighttime expansion (the plant cannot recover to previous size)</li>
            <li>A progressive decline in the minimum diameter night-to-night</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>What the Traffic Light Colors Mean</h2>
          <p>
            Our system analyzes the slope of diameter change over a 72-hour period to categorize 
            plant stress into three levels:
          </p>
          <div className="status-callout status-green">
            <strong>● Green: Healthy</strong>
            <p>Plant is healthy and hydrated. Diameter trend is stable or increasing slightly.</p>
          </div>
          <div className="status-callout status-yellow">
            <strong>● Yellow: Monitor</strong>
            <p>Plant is showing early signs of water stress. Consider checking soil moisture and adjusting watering.</p>
          </div>
          <div className="status-callout status-red">
            <strong>● Red: At Risk</strong>
            <p>Plant is experiencing significant water stress. Watering is recommended soon to prevent wilting or damage.</p>
          </div>
        </div>

        <div className="content-section">
          <h2>Pro Tips for Success</h2>
          <ul>
            <li>Different plants and environmental conditions produce different baseline patterns. Learn your plant's normal rhythm!</li>
            <li>After watering, you should see the plant expand more fully at night, indicating recovery.</li>
            <li>Temperature and humidity affect contraction magnitude—expect more dramatic changes on hot, dry days.</li>
            <li>Consistent monitoring helps you anticipate water needs before visible wilting occurs.</li>
            <li>Keep your clip securely positioned and check alignment regularly for accurate readings.</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
}
